import { Box, Typography, useTheme } from "@mui/material";
import DOMPurify from "isomorphic-dompurify";
import { useLayoutEffect, useMemo, useRef, useState } from "react";
import { Controller } from "react-hook-form";
import ErrorMessageHandle from "../Error/ErrorMessageHandle";

interface CodeFieldProps {
  field: {
    name: string;
    title: string;
    description?: string;
    placeholder?: string;
    validations?: Array<{
      type: string;
      value?: number;
      message: string;
    }>;
    default_value?: string;
  };
  state: Record<string, any>;
  setState: (state: Record<string, any>) => void;
  control: any;
  clearErrors: (name: string) => void;
  error: any;
  data?: string;
  isDisabled?: boolean;
  isRepeater?: boolean;
  handleChange?: (event: React.ChangeEvent<HTMLTextAreaElement>) => void;
}

const parseStoredValue = (value: unknown): string => {
  if (value === null || value === undefined || value === "") return "";

  if (typeof value === "object") {
    try {
      return JSON.stringify(value, null, 2);
    } catch {
      return String(value);
    }
  }

  if (typeof value !== "string") return String(value);

  try {
    const parsed = JSON.parse(value);
    if (typeof parsed === "string") return parsed;
    if (typeof parsed === "object" && parsed !== null) return JSON.stringify(parsed, null, 2);
    return String(parsed);
  } catch {
    return value;
  }
};

const stringifyForStorage = (rawInput: string): string => {
  if (!rawInput) return "";
  try {
    const parsed = JSON.parse(rawInput);
    return JSON.stringify(parsed);
  } catch {
    return JSON.stringify(rawInput);
  }
};

/**
 * Converts raw JSON text into HTML with syntax highlighting.
 * Colors match the image: blue strings, black keys/colons/brackets.
 */
const highlightJson = (code: string): string => {
  if (!code) return "";

  // Escape HTML special characters first
  const escaped = code.replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;");

  // Highlight tokens in order:
  // 1. String values (after colon)  → blue
  // 2. Object keys                  → black bold
  // 3. Numbers / booleans / null    → teal
  // 4. Punctuation stays default black
  return escaped.replace(
    /("(\\u[a-zA-Z0-9]{4}|\\[^u]|[^\\"])*"(\s*:)?|\b(true|false|null)\b|-?\d+(?:\.\d*)?(?:[eE][+-]?\d+)?)/g,
    (match) => {
      if (/^"/.test(match)) {
        if (/:$/.test(match)) {
          // Key — black, no extra color
          return `<span style="color:#000000;font-weight:500">${match}</span>`;
        }
        // String value — blue
        return `<span style="color:#1984bc">${match}</span>`;
      }
      if (/true|false/.test(match)) {
        return `<span style="color:#0070c1">${match}</span>`;
      }
      if (/null/.test(match)) {
        return `<span style="color:#808080">${match}</span>`;
      }
      // Number
      return `<span style="color:#098658">${match}</span>`;
    },
  );
};

// Shared constants — must be identical between textarea and pre
const EDITOR_FONT_FAMILY = "'Courier New', Consolas, monospace";
const EDITOR_FONT_SIZE = "13px";
const EDITOR_LINE_HEIGHT = "21px";
const EDITOR_PADDING = "12px";
const DEFAULT_LINES = 15;

// Calculate default min height: padding-top + padding-bottom + (lines × line-height)
const EDITOR_MIN_HEIGHT = `calc(${DEFAULT_LINES} * ${EDITOR_LINE_HEIGHT} + ${EDITOR_PADDING} * 2)`;

/**
 * SyntaxHighlightEditor
 * Renders a textarea (invisible, captures input) overlaid by a
 * syntax-highlighted <pre> (visible, pointer-events:none).
 * This is the same technique used by CodeMirror and Prism editors.
 * No external packages needed.
 */
const SyntaxHighlightEditor = ({
  value,
  onChange,
  onBlur,
  onFocus,
  isDisabled,
  placeholder,
  fieldName,
}: {
  value: string;
  onChange: (e: React.ChangeEvent<HTMLTextAreaElement>) => void;
  onBlur?: () => void;
  onFocus?: () => void;
  isDisabled: boolean;
  placeholder: string;
  fieldName: string;
}) => {
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const highlightRef = useRef<HTMLPreElement>(null);
  const lineNumberRef = useRef<HTMLDivElement>(null);

  // Always show at least DEFAULT_LINES lines in the gutter
  const lineCount = Math.max(DEFAULT_LINES, value ? value.split("\n").length : DEFAULT_LINES);

  // Save cursor position BEFORE React re-renders the textarea value
  const savedSelectionRef = useRef<{ start: number; end: number } | null>(null);

  const handleChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    savedSelectionRef.current = {
      start: e.target.selectionStart,
      end: e.target.selectionEnd,
    };
    onChange(e);
  };

  // Restore cursor position AFTER React has re-rendered with the new value
  useLayoutEffect(() => {
    const textarea = textareaRef.current;
    if (!textarea || !savedSelectionRef.current) return;
    const { start, end } = savedSelectionRef.current;
    if (document.activeElement === textarea) {
      textarea.setSelectionRange(start, end);
    }
    savedSelectionRef.current = null;
  });

  const handleScroll = () => {
    if (!textareaRef.current) return;
    const { scrollTop, scrollLeft } = textareaRef.current;
    if (highlightRef.current) {
      highlightRef.current.scrollTop = scrollTop;
      highlightRef.current.scrollLeft = scrollLeft;
    }
    if (lineNumberRef.current) {
      lineNumberRef.current.scrollTop = scrollTop;
    }
  };

  const highlighted = useMemo(() => highlightJson(value), [value]);

  return (
    <Box sx={{ display: "flex", minHeight: EDITOR_MIN_HEIGHT, width: "100%" }}>
      {/* Line numbers */}
      <Box
        ref={lineNumberRef}
        aria-hidden='true'
        role='presentation'
        sx={{
          minWidth: "48px",
          width: "48px",
          flexShrink: 0,
          backgroundColor: "#f8f8f8",
          borderRight: "1px solid #e0e0e0",
          paddingTop: EDITOR_PADDING,
          paddingBottom: EDITOR_PADDING,
          paddingRight: "10px",
          userSelect: "none",
          display: "flex",
          flexDirection: "column",
          alignItems: "flex-end",
          overflow: "hidden",
          boxSizing: "border-box",
        }}>
        {Array.from({ length: lineCount }, (_, i) => (
          <Typography
            key={i}
            component='span'
            sx={{
              fontFamily: EDITOR_FONT_FAMILY,
              fontSize: EDITOR_FONT_SIZE,
              lineHeight: EDITOR_LINE_HEIGHT,
              color: "#999999",
              display: "block",
              flexShrink: 0,
            }}>
            {i + 1}
          </Typography>
        ))}
      </Box>

      {/* Editor area */}
      <Box sx={{ flex: 1, position: "relative", overflow: "hidden", minHeight: EDITOR_MIN_HEIGHT }}>
        {/* Syntax-highlighted layer — visual only, no scrollbars */}
        <Box
          ref={highlightRef}
          component='pre'
          aria-hidden='true'
          dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(highlighted || "") }}
          sx={{
            position: "absolute",
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            margin: 0,
            padding: EDITOR_PADDING,
            fontFamily: EDITOR_FONT_FAMILY,
            fontSize: EDITOR_FONT_SIZE,
            lineHeight: EDITOR_LINE_HEIGHT,
            color: "#000000",
            backgroundColor: "transparent",
            whiteSpace: "pre",
            overflow: "hidden",
            wordBreak: "normal",
            pointerEvents: "none",
            tabSize: 2,
            boxSizing: "border-box",
            visibility: value ? "visible" : "hidden",
          }}
        />

        {/* Textarea — transparent text, visible caret */}
        <Box
          ref={textareaRef}
          component='textarea'
          name={fieldName}
          value={value}
          onChange={handleChange}
          onFocus={onFocus}
          onBlur={onBlur}
          onScroll={handleScroll}
          disabled={isDisabled}
          placeholder={placeholder}
          spellCheck={false}
          autoComplete='off'
          autoCorrect='off'
          autoCapitalize='off'
          sx={{
            position: "absolute",
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            width: "100%",
            height: "100%",
            minHeight: EDITOR_MIN_HEIGHT,
            resize: "none",
            border: "none",
            outline: "none",
            // Must exactly match highlight layer
            padding: EDITOR_PADDING,
            fontFamily: EDITOR_FONT_FAMILY,
            fontSize: EDITOR_FONT_SIZE,
            lineHeight: EDITOR_LINE_HEIGHT,
            tabSize: 2,
            whiteSpace: "pre",
            wordBreak: "normal",
            boxSizing: "border-box",
            color: "transparent",
            caretColor: "#333333",
            backgroundColor: "transparent",
            overflowX: "auto",
            overflowY: "auto",
            "&::placeholder": { color: "#aaaaaa" },
            "&:disabled": { cursor: "not-allowed", opacity: 0.6 },
            "&::-webkit-scrollbar": { width: "6px", height: "6px" },
            "&::-webkit-scrollbar-track": { background: "transparent" },
            "&::-webkit-scrollbar-thumb": {
              background: "#cccccc",
              borderRadius: "3px",
            },
            "&::-webkit-scrollbar-thumb:hover": { background: "#aaaaaa" },
            "&::-webkit-scrollbar-corner": { background: "transparent" },
            scrollbarWidth: "thin",
            scrollbarColor: "#cccccc transparent",
          }}
        />
      </Box>
    </Box>
  );
};

const Code = ({
  field,
  state,
  setState,
  control,
  clearErrors,
  error,
  data,
  isDisabled = false,
  isRepeater = false,
  handleChange: externalHandleChange,
}: CodeFieldProps) => {
  const theme = useTheme();
  const [isFocused, setIsFocused] = useState(false);

  const isRequired = field?.validations?.some((v) => v.type === "required") ?? false;
  const maxLength = field?.validations?.find((v) => v.type === "maxLength")?.value;

  const buildValidationRules = () => {
    const rules: Record<string, unknown> = {};

    if (isRequired) {
      rules.required =
        field?.validations?.find((v) => v.type === "required")?.message || "Required";
    }

    if (maxLength) {
      rules.validate = (storedValue: string) => {
        const displayValue = parseStoredValue(storedValue);
        if (displayValue.length > maxLength) {
          return (
            field?.validations?.find((v) => v.type === "maxLength")?.message ||
            `Maximum ${maxLength} characters allowed`
          );
        }
        return true;
      };
    }

    return rules;
  };

  const currentValue = isRepeater ? data || "" : state?.[field.name] || field?.default_value || "";

  const renderCodeEditor = (
    displayValue: string,
    charCount: number,
    onChangeHandler: (e: React.ChangeEvent<HTMLTextAreaElement>) => void,
    onBlurHandler?: () => void,
  ) => (
    <Box sx={{ display: "flex", flexDirection: "column", gap: 0.5 }}>
      <Box
        sx={{
          position: "relative",
          border: "1px solid",
          borderColor: error
            ? theme.palette.error.main
            : isFocused
              ? theme.palette.primary.main
              : theme.palette.divider,
          borderRadius: "4px",
          // Light white background — matches the image
          backgroundColor: error
            ? "#fff5f5"
            : isDisabled
              ? theme.palette.action.disabledBackground
              : "#ffffff",
          overflow: "hidden",
          transition: "border-color 0.2s ease",
        }}>
        <SyntaxHighlightEditor
          value={displayValue}
          onChange={onChangeHandler}
          onFocus={() => setIsFocused(true)}
          onBlur={() => {
            setIsFocused(false);
            onBlurHandler?.();
          }}
          isDisabled={isDisabled}
          placeholder={
            field?.placeholder ||
            'Enter your code here...\ne.g. { "address": { "street": "123 Main St" } }'
          }
          fieldName={field.name}
        />
      </Box>

      {maxLength && (
        <Box sx={{ display: "flex", justifyContent: "flex-end", px: 0.5 }}>
          <Typography
            variant='caption'
            sx={{
              color:
                charCount > maxLength ? theme.palette.error.main : theme.palette.text.secondary,
            }}>
            {charCount}/{maxLength}
          </Typography>
        </Box>
      )}
    </Box>
  );

  // Repeater mode
  if (isRepeater) {
    const displayValue = parseStoredValue(currentValue);
    const charCount = displayValue.length;

    const onChangeHandler = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
      const rawValue = e.target.value;
      const storedValue = stringifyForStorage(rawValue);

      const syntheticEvent = {
        ...e,
        target: {
          ...e.target,
          name: field.name,
          value: storedValue,
        },
      } as React.ChangeEvent<HTMLTextAreaElement>;

      if (externalHandleChange) {
        externalHandleChange(syntheticEvent);
      }
    };

    return (
      <Box id={`${field?.name}_id`}>
        {renderCodeEditor(displayValue, charCount, onChangeHandler)}
        <ErrorMessageHandle error={error} />
      </Box>
    );
  }

  // Standalone mode
  return (
    <Box id={`${field?.name}_id`}>
      <Controller
        name={field.name}
        control={control}
        defaultValue={currentValue}
        rules={buildValidationRules()}
        render={({ field: controllerField }) => {
          const displayValue = parseStoredValue(controllerField.value);
          const charCount = displayValue.length;

          const onChangeHandler = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
            const rawValue = e.target.value;
            const storedValue = stringifyForStorage(rawValue);

            if (error) {
              clearErrors(field.name);
            }

            controllerField.onChange(storedValue);
            setState({
              ...state,
              [field.name]: storedValue,
            });
          };

          return renderCodeEditor(displayValue, charCount, onChangeHandler, controllerField.onBlur);
        }}
      />
      <ErrorMessageHandle error={error} />
    </Box>
  );
};

export default Code;
